import React, { Children, ReactElement } from 'react';

import { Icon, TIconNames } from '@pb/shared/ui/atoms/icon';
import {
    TNavigationItemChildren,
    TNavigationItemProperties,
    TRenderItemProperties,
} from './navigation-item.types';
import {
    StyledDesktopArrowIconContainer,
    StyledIconContainer,
    StyledList,
    StyledListItem,
    StyledMobileArrowIconContainer,
    StyledNavigationItem,
    StyledTitle,
} from './navigation-item.styled';

function renderChildren(
    isStandalone: boolean,
    children: TNavigationItemChildren
): ReactElement {
    return (
        <StyledList isStandalone={isStandalone}>
            {/* eslint-disable-next-line unicorn/no-fn-reference-in-iterator */}
            {Children.map(children, child =>
                React.cloneElement(child, {
                    isStandalone,
                    level: 2,
                })
            )}
        </StyledList>
    );
}

function getItemType(isStandalone: boolean, isExpanded: boolean): TIconNames {
    if (isStandalone) {
        return 'removeOrClose';
    }

    return isExpanded ? 'arrowDown' : 'arrowRight';
}

function renderItem({
    icon,
    level,
    label,
    hasChildren,
    isExpanded,
    isStandalone,
    onClose,
    href,
}: TRenderItemProperties): ReactElement {
    return (
        <StyledNavigationItem
            as={!href ? 'button' : 'a'}
            level={level}
            isStandalone={isStandalone}
            isExpanded={isExpanded}
            href={href}
            onClick={() => {
                if (isStandalone && typeof onClose === 'function') {
                    onClose();
                }
            }}
        >
            <StyledMobileArrowIconContainer>
                {hasChildren && (
                    <Icon
                        type={getItemType(isStandalone, isExpanded)}
                        color="darkPurple"
                        size={18}
                    />
                )}
            </StyledMobileArrowIconContainer>
            {icon && (
                <StyledIconContainer>
                    <Icon type={icon} color="black" size={18} />
                </StyledIconContainer>
            )}
            <StyledTitle level={level} isStandalone={isStandalone}>
                {label}
            </StyledTitle>
            {hasChildren && (
                <StyledDesktopArrowIconContainer>
                    <Icon type="arrowDown" color="darkPurple" size={18} />
                </StyledDesktopArrowIconContainer>
            )}
        </StyledNavigationItem>
    );
}

export function NavigationItem({
    children,
    icon,
    level = 1,
    onClose,
    isStandalone = false,
    label,
    href,
    isExpanded = false,
    onClick,
}: TNavigationItemProperties): ReactElement {
    const hasChildren = Children.count(children) > 0;
    const shouldRenderChildren = hasChildren && isExpanded;

    return (
        <StyledListItem
            onClick={hasChildren && onClick ? () => onClick(label) : undefined}
        >
            {renderItem({
                icon,
                level,
                label,
                hasChildren,
                isStandalone,
                isExpanded,
                href,
                onClose,
            })}
            {shouldRenderChildren &&
                renderChildren(
                    isStandalone,
                    children as TNavigationItemChildren
                )}
        </StyledListItem>
    );
}
